/**
 * Pipeline — simplified configuration page.
 *
 * 3 tabs:
 * - Monitors: grouped by strategy (Company Monitors, Intelligence Scanners,
 *   Trend Watchers, Discovery & Research). Each owns its schedule.
 * - Entity Groups: watchlist management (static + dynamic).
 * - System Config: read-only reference for Sources, Products, Routing Rules.
 *
 * Kill-switch bar at top controls the scheduler master on/off.
 */

import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import {
  Radio, Users, CheckCircle2, XCircle, Plus, Trash2, X,
  ChevronRight, ChevronDown, Search, Play, Loader2, RefreshCw,
  Power, Database, Package, GitBranch, Settings, Zap,
} from 'lucide-react';
import {
  listContentNeeds, createContentNeed, updateContentNeed, deactivateContentNeed,
  listEntityGroups, createEntityGroup, updateEntityGroup,
  getGroupMembers, addGroupMembers, removeGroupMember, resolveGroup,
  triggerManualFetch, getIngestionStats,
  getSchedulerConfig, updateSchedulerConfig,
  getPipelineHealth, getNeedNextFire, previewSchedule,
} from '../lib/api/ingress.js';
import { listContentProducts, listRoutingRules, listIngressSources, updateContentProduct } from '../lib/api/content.js';
import { listEntities, lookupEntity, createEntity } from '../lib/api/entities.js';
import type {
  ContentNeed, ContentNeedCreate,
  EntityGroup, EntityGroupUpdate, CoveragePolicy, EditorialGuarantee,
  NeedScheduleConfig, ScheduleWindow, ScheduleCadence,
} from '../lib/api/ingress.js';
import type { IngressSource } from '../lib/api/content.js';
import { Modal, ModalHeader, ModalBody, ModalFooter } from '../components/ui/modal.js';

type Tab = 'monitors' | 'groups' | 'config';

const TABS: { key: Tab; label: string; icon: typeof Radio }[] = [
  { key: 'monitors', label: 'Monitors', icon: Radio },
  { key: 'groups', label: 'Entity Groups', icon: Users },
  { key: 'config', label: 'System Config', icon: Settings },
];

// Strategy type grouping
type StrategyGroup = 'company' | 'scanner' | 'trend' | 'discovery';
const STRATEGY_GROUPS: { key: StrategyGroup; label: string; needTypes: string[] }[] = [
  { key: 'company', label: 'Company Monitors', needTypes: ['company_coverage', 'sector_coverage'] },
  { key: 'scanner', label: 'Intelligence Scanners', needTypes: ['content_type', 'intraday_discovery'] },
  { key: 'trend', label: 'Trend Watchers', needTypes: ['trend_reactive'] },
  { key: 'discovery', label: 'Discovery & Research', needTypes: ['research', 'backfill', 'gap_fill'] },
];

/** Safely parse JSONB fields — asyncpg may return as string instead of object. */
function parseJsonb<T = Record<string, any>>(raw: any): T {
  // The API returns jsonb columns as raw asyncpg strings, and past
  // double-encoding bugs left some rows JSON-encoded twice. Unwrap up to a
  // few string layers, then REQUIRE a plain object — never return a string.
  // Returning a string here is what let `{...parseJsonb(x)}` char-explode a
  // value into {"0":"{","1":...} (the 17 MB match_criteria corruption).
  let v: any = raw;
  for (let i = 0; i < 4 && typeof v === 'string'; i++) {
    if (!v) return {} as T;
    try { v = JSON.parse(v); } catch { return {} as T; }
  }
  return (v && typeof v === 'object' && !Array.isArray(v)) ? (v as T) : ({} as T);
}

const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri'];

/** A fresh window with sensible defaults for the window-list editor. */
function newWindow(partial?: Partial<ScheduleWindow>): ScheduleWindow {
  return {
    days: [...WEEKDAYS],
    start: '07:00',
    end: '16:00',
    cadence: { type: 'interval', minutes: 30, anchor_minute: 0 },
    ...partial,
  };
}

/** Compact day-set label: "Mo-Fr" / "Daily" / "Mo·We·Fr". */
function daysLabel(days: string[]): string {
  const s = [...(days || [])].sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b));
  if (s.length === 7) return 'Daily';
  if (s.length === 5 && WEEKDAYS.every(d => s.includes(d))) return 'Mo-Fr';
  return s.map(d => d.charAt(0).toUpperCase() + d.charAt(1)).join('·');
}

/** One-line summary of a window for the monitor card. */
function summariseWindow(w: ScheduleWindow): string {
  const days = daysLabel(w.days || []);
  if (w.cadence?.type === 'explicit') {
    return `${days} @${(w.cadence.times || []).join(',') || '—'}`;
  }
  return `${days} ${w.start}-${w.end} ·${w.cadence?.minutes ?? '?'}m`;
}

// Type presets for new monitors — v2 windowed schedules.
const TYPE_PRESETS: Record<string, { sources: string[]; schedule: NeedScheduleConfig; priority: number }> = {
  company_coverage: {
    sources: ['market_index', 'asx_direct'],
    schedule: {
      version: 2, timezone: 'Australia/Sydney',
      windows: [newWindow({ label: 'Trading day', start: '09:30', end: '16:30', cadence: { type: 'interval', minutes: 30, anchor_minute: 0 } })],
    },
    priority: 50,
  },
  content_type: {
    sources: ['market_index', 'asx_direct'],
    schedule: {
      version: 2, timezone: 'Australia/Sydney',
      windows: [newWindow({ label: 'Trading day', start: '09:30', end: '16:30', cadence: { type: 'interval', minutes: 30, anchor_minute: 0 } })],
    },
    priority: 25,
  },
  trend_reactive: {
    sources: [],   // HotCopper retired 2026-10-06 — pick a source per need
    schedule: {
      version: 2, timezone: 'Australia/Sydney',
      windows: [newWindow({ label: 'All hours', days: [...DAYS], start: '00:00', end: '23:59', cadence: { type: 'interval', minutes: 60, anchor_minute: 0 } })],
    },
    priority: 20,
  },
  research: {
    sources: [],
    schedule: {
      version: 2, timezone: 'Australia/Sydney',
      windows: [newWindow({ label: 'Business hours', start: '09:00', end: '17:00', cadence: { type: 'interval', minutes: 360, anchor_minute: 0 } })],
    },
    priority: 70,
  },
};


export function PipelinePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as Tab) || 'monitors';
  const setActiveTab = (tab: Tab) => setSearchParams({ tab });
  const queryClient = useQueryClient();

  const configQuery = useQuery({ queryKey: ['scheduler-config'], queryFn: getSchedulerConfig });
  const configMut = useMutation({
    mutationFn: updateSchedulerConfig,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scheduler-config'] });
      queryClient.invalidateQueries({ queryKey: ['pipeline-health'] });
    },
  });
  const healthQuery = useQuery({
    queryKey: ['pipeline-health'],
    queryFn: getPipelineHealth,
    refetchInterval: 30_000,
  });

  const needsQuery = useQuery({ queryKey: ['content-needs'], queryFn: () => listContentNeeds(false) });
  const groupsQuery = useQuery({ queryKey: ['entity-groups'], queryFn: () => listEntityGroups(false) });
  const sourcesQuery = useQuery({ queryKey: ['ingress-sources'], queryFn: listIngressSources });
  const productsQuery = useQuery({ queryKey: ['content-products'], queryFn: listContentProducts });
  const rulesQuery = useQuery({ queryKey: ['routing-rules'], queryFn: listRoutingRules });
  const statsQuery = useQuery({ queryKey: ['ingestion-stats'], queryFn: getIngestionStats });

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
      {/* Header + Kill Switch */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-grey-900">Pipeline</h1>
          {statsQuery.data && (
            <p className="text-sm text-grey-500 mt-0.5">
              {statsQuery.data.ingested_today} ingested today, {statsQuery.data.fulfilled_today} fulfilled
            </p>
          )}
        </div>
        {configQuery.data && (
          <KillSwitchBar
            config={configQuery.data}
            onToggle={(updates) => configMut.mutate(updates)}
            health={healthQuery.data}
          />
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-grey-200">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          const count = tab.key === 'monitors' ? needsQuery.data?.length
            : tab.key === 'groups' ? groupsQuery.data?.length : undefined;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                isActive ? 'border-primary-600 text-primary-700' : 'border-transparent text-grey-500 hover:text-grey-700'
              }`}>
              <Icon className="h-4 w-4" />
              {tab.label}
              {count !== undefined && <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${isActive ? 'bg-primary-100 text-primary-700' : 'bg-grey-100 text-grey-500'}`}>{count}</span>}
            </button>
          );
        })}
      </div>

      {activeTab === 'monitors' && <MonitorsTab needs={needsQuery.data || []} sources={sourcesQuery.data || []} groups={groupsQuery.data || []} isLoading={needsQuery.isLoading} />}
      {activeTab === 'groups' && <EntityGroupsTab groups={groupsQuery.data || []} isLoading={groupsQuery.isLoading} />}
      {activeTab === 'config' && <SystemConfigTab sources={sourcesQuery.data || []} products={productsQuery.data || []} rules={rulesQuery.data || []} isLoading={sourcesQuery.isLoading} />}
    </div>
  );
}


// ============================================================================
// PER-NEED NEXT-FIRE ROW — chip list of the next planned fire instants
// ============================================================================

function NeedNextFireRow({ needId }: { needId: string }) {
  const { data } = useQuery({
    queryKey: ['need-next-fire', needId],
    queryFn: () => getNeedNextFire(needId),
    refetchInterval: 60_000,
  });
  if (!data || !data.upcoming_fires?.length) return null;
  return (
    <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
      <span className="text-[10px] text-grey-400 uppercase font-semibold">Next</span>
      {data.upcoming_fires.map((t, i) => (
        <span
          key={t}
          className={`px-1.5 py-0.5 rounded text-[10px] font-mono ${
            i === 0 ? 'bg-primary-100 text-primary-700 font-semibold' : 'bg-grey-100 text-grey-500'
          }`}
        >
          {t}
        </span>
      ))}
    </div>
  );
}


// ============================================================================
// KILL-SWITCH BAR — three master toggles + 24h auto-promote counters
// ============================================================================

interface KillSwitchBarProps {
  config: import('../lib/api/ingress.js').SchedulerConfig;
  health?: import('../lib/api/ingress.js').PipelineHealth;
  onToggle: (updates: Partial<import('../lib/api/ingress.js').SchedulerConfig>) => void;
}

function KillSwitchBar({ config, health, onToggle }: KillSwitchBarProps) {
  const switches: Array<{
    key: 'is_enabled' | 'publishing_enabled' | 'auto_promotion_enabled';
    label: string;
    on: string;
    off: string;
    confirm: string;
  }> = [
    {
      key: 'is_enabled',
      label: 'Scheduler',
      on: 'Running',
      off: 'Stopped',
      confirm: 'Stop the ingestion scheduler? New ingest jobs will not be created until you restart it.',
    },
    {
      key: 'publishing_enabled',
      label: 'Publishing',
      on: 'On',
      off: 'Paused',
      confirm: 'Pause auto-publish? Articles will reach approved but no publish_job will be queued until you re-enable.',
    },
    {
      key: 'auto_promotion_enabled',
      label: 'Auto-Promotion',
      on: 'On',
      off: 'Paused',
      confirm: 'Pause auto-promotion? Successful test publishes will stay on test until you re-enable. Manual promote is unaffected.',
    },
  ];

  const handleToggle = (
    key: 'is_enabled' | 'publishing_enabled' | 'auto_promotion_enabled',
    next: boolean,
    confirmCopy: string,
  ) => {
    if (!next) {
      // Confirm before turning a switch OFF — turning back on is risk-free.
      // eslint-disable-next-line no-alert
      if (!confirm(confirmCopy)) return;
    }
    onToggle({ [key]: next } as Partial<import('../lib/api/ingress.js').SchedulerConfig>);
  };

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {switches.map(({ key, label, on, off, confirm }) => {
        const value = (config as any)[key];
        const isOn = value === undefined ? true : value;
        return (
          <div key={key} className="flex items-center gap-1.5">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg ${
                isOn ? 'bg-success-50 text-success-700' : 'bg-grey-100 text-grey-500'
              }`}
            >
              <Power className="h-3.5 w-3.5" />
              {label}: {isOn ? on : off}
            </span>
            <button
              onClick={() => handleToggle(key, !isOn, confirm)}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-lg ${
                isOn
                  ? 'bg-danger-50 text-danger-700 hover:bg-danger-100'
                  : 'bg-success-50 text-success-700 hover:bg-success-100'
              }`}
            >
              {isOn ? 'Pause' : 'Resume'}
            </button>
          </div>
        );
      })}
      {health?.auto_promote_24h && (
        <div
          className="flex items-center gap-2 ml-2 pl-3 border-l border-grey-200 text-xs text-grey-600"
          title="Auto-promote outcomes in the last 24h"
        >
          <span className="font-semibold text-success-700">{health.auto_promote_24h.totals.queued} queued</span>
          <span className="text-grey-500">{health.auto_promote_24h.totals.skipped} skipped</span>
          {health.auto_promote_24h.totals.enqueue_failed > 0 && (
            <span className="text-danger-700 font-semibold">
              {health.auto_promote_24h.totals.enqueue_failed} failed
            </span>
          )}
          <span className="text-grey-400">/ 24h</span>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// MONITORS TAB — grouped by strategy type
// ============================================================================

function MonitorsTab({ needs, sources, groups, isLoading }: {
  needs: ContentNeed[]; sources: IngressSource[]; groups: EntityGroup[]; isLoading: boolean;
}) {
  const [showModal, setShowModal] = useState(false);
  const [editingNeed, setEditingNeed] = useState<ContentNeed | null>(null);
  const [createType, setCreateType] = useState<string>('company_coverage');
  const queryClient = useQueryClient();

  const deactivateMut = useMutation({
    mutationFn: deactivateContentNeed,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['content-needs'] }),
    onError: (err: any) => {
      const msg = err?.response?.data?.detail || err?.message || 'unknown error';
      // eslint-disable-next-line no-alert
      alert(`Pause failed: ${msg}`);
      // eslint-disable-next-line no-console
      console.error('deactivate content need failed', err);
    },
  });
  const reactivateMut = useMutation({
    mutationFn: (id: string) => updateContentNeed(id, { is_active: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['content-needs'] }),
    onError: (err: any) => {
      const msg = err?.response?.data?.detail || err?.message || 'unknown error';
      // eslint-disable-next-line no-alert
      alert(`Resume failed: ${msg}`);
      // eslint-disable-next-line no-console
      console.error('reactivate content need failed', err);
    },
  });
  const fetchMut = useMutation({
    mutationFn: triggerManualFetch,
    onError: (err: any) => {
      const msg = err?.response?.data?.detail || err?.message || 'unknown error';
      // eslint-disable-next-line no-alert
      alert(`Fetch failed: ${msg}`);
      // eslint-disable-next-line no-console
      console.error('manual fetch failed', err);
    },
  });

  // Group needs by strategy — must be above any early returns (React hooks rule)
  const grouped = useMemo(() => {
    const result: Record<StrategyGroup, ContentNeed[]> = { company: [], scanner: [], trend: [], discovery: [] };
    for (const need of (needs || [])) {
      const group = STRATEGY_GROUPS.find(g => g.needTypes.includes(need.need_type));
      if (group) result[group.key].push(need);
      else result.discovery.push(need);
    }
    return result;
  }, [needs]);

  if (isLoading) return <Loading />;

  const openCreate = (type: string) => {
    setCreateType(type);
    setEditingNeed(null);
    setShowModal(true);
  };

  return (
    <>
      {STRATEGY_GROUPS.map(sg => {
        const items = grouped[sg.key];
        const defaultType = sg.needTypes[0];
        return (
          <div key={sg.key} className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-grey-600">{sg.label} ({items.length})</h3>
              <button onClick={() => openCreate(defaultType)}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium">
                <Plus className="h-3 w-3" /> Add
              </button>
            </div>
            {items.length === 0 ? (
              <p className="text-xs text-grey-400 italic py-2">None configured</p>
            ) : (
              <div className="space-y-2">
                {items.map(need => (
                  <MonitorCard key={need.need_id} need={need}
                    onEdit={() => { setEditingNeed(need); setShowModal(true); }}
                    onPause={() => deactivateMut.mutate(need.need_id)}
                    onResume={() => reactivateMut.mutate(need.need_id)}
                    onFetch={(src) => fetchMut.mutate(src)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {showModal && (
        <MonitorModal
          key={editingNeed?.need_id || 'new'}
          need={editingNeed}
          defaultType={createType}
          availableSources={sources}
          availableGroups={groups}
          onClose={() => { setShowModal(false); setEditingNeed(null); }}
        />
      )}
    </>
  );
}

function MonitorCard({ need, onEdit, onPause, onResume, onFetch }: {
  need: ContentNeed; onEdit: () => void; onPause: () => void; onResume: () => void; onFetch: (src: string) => void;
}) {
  const sched = parseJsonb<NeedScheduleConfig>(need.schedule_config);
  const sp = parseJsonb(need.source_preferences);
  const mc = parseJsonb(need.match_criteria);
  const srcNames = (sp.preferred_sources || []) as string[];
  const entityGroups = (mc.entity_groups || []) as string[];
  const keywords = (mc.keywords || []) as string[];
  const onMatch = mc.on_match;

  return (
    <div className={`bg-surface rounded-lg border p-3 ${need.is_active ? 'border-grey-200' : 'border-grey-100 opacity-60'}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={onEdit} className="text-sm font-semibold text-grey-900 hover:text-primary-600">{need.need_name}</button>
            <TypeBadge type={need.need_type} />
            <span className="text-xs text-grey-400">P{need.priority}</span>
            {need.need_name.startsWith('coverage:') && (
              <span className="px-1.5 py-0.5 text-[10px] rounded bg-primary-50 text-primary-600" title="Auto-provisioned from the group's coverage policy — edit it on the Entity Groups tab, not here (changes will be overwritten).">managed by group policy</span>
            )}
            {!need.is_active && <span className="px-1.5 py-0.5 text-[10px] rounded bg-grey-100 text-grey-400">Paused</span>}
            {onMatch && onMatch !== 'always_generate' && (
              <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-50 text-amber-600">{onMatch === 'record_signal' ? 'Signal only' : 'If significant'}</span>
            )}
          </div>
          <div className="flex flex-wrap gap-1 mt-1">
            {entityGroups.map(g => <span key={g} className="px-1.5 py-0.5 text-[10px] rounded bg-purple-50 text-purple-600">{g}</span>)}
            {keywords.map(k => <span key={k} className="px-1.5 py-0.5 text-[10px] rounded bg-amber-50 text-amber-600">{k}</span>)}
            {srcNames.map(s => <span key={s} className="px-1.5 py-0.5 text-[10px] rounded bg-grey-100 text-grey-500">{s}</span>)}
          </div>
          <div className="flex gap-3 mt-1 text-xs text-grey-500 flex-wrap">
            {sched.version === 2 && sched.windows?.length ? (
              <>
                {sched.windows.map((w, i) => (
                  <span key={i}>{summariseWindow(w)}</span>
                ))}
                {sched.timezone && <span className="text-grey-400">{sched.timezone.replace(/^.*\//, '')}</span>}
              </>
            ) : (
              <span className="text-warning-500">Legacy schedule — re-save to migrate</span>
            )}
            {need.last_fetched_at && <span>Last: {format(new Date(need.last_fetched_at), 'dd MMM HH:mm')}</span>}
            <span>{need.items_fulfilled} fulfilled</span>
          </div>
          {sched.version === 2 && need.is_active && <NeedNextFireRow needId={need.need_id} />}
        </div>
        <div className="flex items-center gap-1 ml-3">
          {srcNames[0] && <button onClick={() => onFetch(srcNames[0])} className="p-1 text-grey-400 hover:text-primary-600" title="Fetch now"><Play className="h-3.5 w-3.5" /></button>}
          {need.is_active
            ? <button onClick={onPause} className="p-1 text-grey-400 hover:text-danger-600" title="Pause"><XCircle className="h-3.5 w-3.5" /></button>
            : <button onClick={onResume} className="p-1 text-grey-400 hover:text-success-600" title="Resume"><RefreshCw className="h-3.5 w-3.5" /></button>
          }
        </div>
      </div>
    </div>
  );
}



// ============================================================================
// MONITOR MODAL — type-specific forms
// ============================================================================

function MonitorModal({ need, defaultType, availableSources, availableGroups, onClose }: {
  need: ContentNeed | null; defaultType: string; availableSources: IngressSource[]; availableGroups: EntityGroup[]; onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const isEditing = !!need;
  const needType = need?.need_type || defaultType;
  const isScanner = needType === 'content_type';

  // Preserve unknown match_criteria fields — parse in case JSONB came back as string
  const existingMC = parseJsonb(need?.match_criteria);

  const [name, setName] = useState(need?.need_name || '');
  const [priority, setPriority] = useState(need?.priority ?? (TYPE_PRESETS[needType]?.priority ?? 50));

  // Entity scope
  const [selectedGroups, setSelectedGroups] = useState<string[]>(existingMC.entity_groups || []);
  const [companyCodes, setCompanyCodes] = useState<string[]>(existingMC.company_codes || []);
  const [codeInput, setCodeInput] = useState('');

  // Scanner-specific
  const [keywords, setKeywords] = useState<string[]>(existingMC.keywords || []);
  const [keywordInput, setKeywordInput] = useState('');
  const [priceSensitiveOnly, setPriceSensitiveOnly] = useState<boolean>(existingMC.price_sensitive_only || false);
  const [onMatch, setOnMatch] = useState<string>(existingMC.on_match || 'always_generate');
  const [scopeMode, setScopeMode] = useState<string>(
    !existingMC.entity_groups?.length ? 'unfiltered' : existingMC.entity_groups?.includes('asx_all_approved') ? 'all' : 'specific'
  );

  // Sources
  const existingSP = parseJsonb(need?.source_preferences);
  const preset = TYPE_PRESETS[needType];
  const [selectedSources, setSelectedSources] = useState<string[]>(existingSP.preferred_sources || preset?.sources || []);

  // Schedule — v2 windowed model. A saved need is "on v2" when its
  // schedule_config carries version:2 / windows[]; legacy v1 needs (or
  // a brand-new monitor) fall through to the type preset's windows.
  const dbSched = parseJsonb<NeedScheduleConfig>(need?.schedule_config);
  const hasV2Schedule = dbSched.version === 2 && Array.isArray(dbSched.windows);
  const sched: NeedScheduleConfig =
    (isEditing && hasV2Schedule) ? dbSched : (preset?.schedule || { version: 2, windows: [newWindow()] });
  const [timezone, setTimezone] = useState(sched.timezone || 'Australia/Sydney');
  const [windows, setWindows] = useState<ScheduleWindow[]>(
    (sched.windows && sched.windows.length) ? sched.windows.map(w => ({ ...w })) : [newWindow()],
  );

  const [dateMode, setDateMode] = useState(need?.date_mode || 'publish_date');
  const [dailyLimit, setDailyLimit] = useState(need?.daily_limit?.toString() || '');
  const [totalLimit, setTotalLimit] = useState(need?.total_limit?.toString() || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Live "next fires" preview of the in-progress (unsaved) window set.
  const [previewFires, setPreviewFires] = useState<string[]>([]);
  useEffect(() => {
    const cfg: NeedScheduleConfig = { version: 2, timezone, windows };
    const t = setTimeout(() => {
      previewSchedule(cfg)
        .then(r => setPreviewFires(r.upcoming_fires || []))
        .catch(() => setPreviewFires([]));
    }, 350);
    return () => clearTimeout(t);
  }, [timezone, windows]);

  /** Returns a human error string if the window set is invalid, else null.
   *  MIN_CADENCE_MINUTES = 2× the 5-min scheduler tick (see plan). */
  const MIN_CADENCE_MINUTES = 10;
  const validateWindows = (): string | null => {
    if (windows.length === 0) return 'Add at least one polling window (or the monitor never fires).';
    for (let i = 0; i < windows.length; i++) {
      const w = windows[i];
      const n = `Window ${i + 1}`;
      if (!w.days || w.days.length === 0) return `${n}: pick at least one day.`;
      if (w.cadence.type === 'interval') {
        if (!w.start || !w.end) return `${n}: set a start and end time.`;
        if ((w.cadence.minutes ?? 0) < MIN_CADENCE_MINUTES)
          return `${n}: interval must be at least ${MIN_CADENCE_MINUTES} min (twice the scheduler tick).`;
      } else {
        if (!w.cadence.times || w.cadence.times.length === 0)
          return `${n}: add at least one exact fire time.`;
      }
    }
    return null;
  };

  // Window-list mutators — all immutable so React re-renders + the
  // preview effect re-runs.
  const patchWindow = (i: number, patch: Partial<ScheduleWindow>) =>
    setWindows(ws => ws.map((w, idx) => (idx === i ? { ...w, ...patch } : w)));
  const patchCadence = (i: number, patch: Partial<ScheduleCadence>) =>
    setWindows(ws => ws.map((w, idx) => (idx === i ? { ...w, cadence: { ...w.cadence, ...patch } } : w)));
  const removeWindow = (i: number) => setWindows(ws => ws.filter((_, idx) => idx !== i));
  const addWindow = () => setWindows(ws => [...ws, newWindow()]);
  const toggleWindowDay = (i: number, day: string) =>
    setWindows(ws => ws.map((w, idx) => (idx === i
      ? { ...w, days: w.days.includes(day) ? w.days.filter(d => d !== day) : [...w.days, day] }
      : w)));
  const setCadenceType = (i: number, type: 'interval' | 'explicit') =>
    setWindows(ws => ws.map((w, idx) => (idx === i
      ? {
          ...w,
          cadence: type === 'interval'
            ? { type: 'interval', minutes: w.cadence.minutes ?? 30, anchor_minute: w.cadence.anchor_minute ?? 0 }
            : { type: 'explicit', times: w.cadence.times ?? [] },
        }
      : w)));

  const handleSubmit = async () => {
    setError('');
    const scheduleErr = validateWindows();
    if (scheduleErr) { setError(scheduleErr); return; }
    setIsSubmitting(true);
    try {
      // Build match_criteria preserving unknown fields
      const newMC: Record<string, any> = { ...existingMC };
      // Set entity groups based on scope mode
      if (isScanner) {
        if (scopeMode === 'all') newMC.entity_groups = ['asx_all_approved'];
        else if (scopeMode === 'specific') newMC.entity_groups = selectedGroups;
        else delete newMC.entity_groups; // unfiltered
        newMC.keywords = keywords;
        newMC.on_match = onMatch;
      } else {
        newMC.entity_groups = selectedGroups.length > 0 ? selectedGroups : undefined;
        delete newMC.company_codes;
        delete newMC.keywords;
        delete newMC.on_match;
      }
      if (priceSensitiveOnly) newMC.price_sensitive_only = true;
      else delete newMC.price_sensitive_only;
      // Clean undefined
      Object.keys(newMC).forEach(k => { if (newMC[k] === undefined) delete newMC[k]; });

      const body: any = {
        need_name: name,
        match_criteria: newMC,
        source_preferences: { preferred_sources: selectedSources },
        date_mode: dateMode,
        priority,
        daily_limit: dailyLimit ? parseInt(dailyLimit) : null,
        total_limit: totalLimit ? parseInt(totalLimit) : null,
        schedule_config: { version: 2, timezone, windows },
      };

      if (isEditing) {
        await updateContentNeed(need!.need_id, body);
      } else {
        body.need_type = needType;
        await createContentNeed(body as ContentNeedCreate);
      }
      await queryClient.invalidateQueries({ queryKey: ['content-needs'] });
      onClose();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail
        : Array.isArray(detail) ? detail.map((d: any) => d.msg || JSON.stringify(d)).join('; ')
        : (e.message || 'Failed to save');
      console.error('Monitor save error:', { status: e?.response?.status, data: e?.response?.data, message: e?.message });
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const strategyLabel = STRATEGY_GROUPS.find(g => g.needTypes.includes(needType))?.label || needType;

  return (
    <Modal onClose={onClose} size="2xl">
      <ModalHeader>
        <div>
          <h2 className="text-lg font-bold text-grey-900">{isEditing ? 'Edit' : 'New'} {isScanner ? 'Scanner' : 'Monitor'}</h2>
          <span className="text-xs text-grey-500">{strategyLabel}</span>
        </div>
        <button onClick={onClose} className="text-grey-400 hover:text-grey-600 touch-target"><X className="h-5 w-5" /></button>
      </ModalHeader>

      <ModalBody className="space-y-5">
          {error && <div className="p-3 bg-danger-50 text-danger-700 rounded-lg text-sm">{error}</div>}

          {/* Identity */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-grey-600 mb-1">Name</label>
              <input value={name} onChange={e => setName(e.target.value)} className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-grey-600 mb-1">Priority</label>
              <input type="number" value={priority} onChange={e => setPriority(Number(e.target.value))} min={1} max={100} className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>

          {/* SCANNER: Scope + What to find + Action */}
          {isScanner && (
            <>
              <div className="border-t border-grey-100 pt-4">
                <h3 className="text-xs font-semibold text-grey-500 uppercase mb-3">Scope</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="scope" value="all" checked={scopeMode === 'all'} onChange={() => setScopeMode('all')} className="h-3.5 w-3.5" />
                    All ASX entities
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="scope" value="specific" checked={scopeMode === 'specific'} onChange={() => setScopeMode('specific')} className="h-3.5 w-3.5" />
                    Specific entity groups
                  </label>
                  {scopeMode === 'specific' && (
                    <div className="ml-6 flex flex-wrap gap-1.5">
                      {availableGroups.filter(g => g.is_active).map(g => (
                        <button key={g.group_id}
                          onClick={() => setSelectedGroups(prev => prev.includes(g.group_name) ? prev.filter(x => x !== g.group_name) : [...prev, g.group_name])}
                          className={`px-2 py-1 text-xs rounded-lg ${selectedGroups.includes(g.group_name) ? 'bg-purple-100 text-purple-700 font-medium' : 'bg-grey-100 text-grey-500 hover:bg-grey-200'}`}>
                          {g.group_name}
                        </button>
                      ))}
                    </div>
                  )}
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="scope" value="unfiltered" checked={scopeMode === 'unfiltered'} onChange={() => setScopeMode('unfiltered')} className="h-3.5 w-3.5" />
                    Unfiltered (scan everything)
                  </label>
                </div>
              </div>

              <div className="border-t border-grey-100 pt-4">
                <h3 className="text-xs font-semibold text-grey-500 uppercase mb-3">What to find</h3>
                <div className="space-y-2">
                  <div>
                    <label className="block text-xs text-grey-600 mb-1">Keywords</label>
                    <div className="flex flex-wrap gap-1 items-center">
                      {keywords.map(k => (
                        <span key={k} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-50 text-amber-700 rounded">
                          {k} <button onClick={() => setKeywords(prev => prev.filter(x => x !== k))} className="text-amber-400 hover:text-amber-600"><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                      <input value={keywordInput} onChange={e => setKeywordInput(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && keywordInput.trim()) { setKeywords(prev => [...new Set([...prev, keywordInput.trim().toLowerCase()])]); setKeywordInput(''); } }}
                        placeholder="Type keyword + Enter" className="w-32 border border-grey-200 rounded px-2 py-0.5 text-xs" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-grey-600">
                    <input type="checkbox" checked={priceSensitiveOnly} onChange={e => setPriceSensitiveOnly(e.target.checked)} className="h-3.5 w-3.5 rounded" />
                    Price sensitive only
                  </label>
                </div>
              </div>

              <div className="border-t border-grey-100 pt-4">
                <h3 className="text-xs font-semibold text-grey-500 uppercase mb-3">When found</h3>
                <div className="space-y-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="action" value="record_signal" checked={onMatch === 'record_signal'} onChange={() => setOnMatch('record_signal')} className="h-3.5 w-3.5" />
                    Record signal only
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="action" value="generate_if_significant" checked={onMatch === 'generate_if_significant'} onChange={() => setOnMatch('generate_if_significant')} className="h-3.5 w-3.5" />
                    Generate article if price-sensitive
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="action" value="always_generate" checked={onMatch === 'always_generate'} onChange={() => setOnMatch('always_generate')} className="h-3.5 w-3.5" />
                    Always generate article
                  </label>
                </div>
              </div>
            </>
          )}

          {/* COMPANY MONITOR: Entity groups only */}
          {!isScanner && (
            <div className="border-t border-grey-100 pt-4">
              <h3 className="text-xs font-semibold text-grey-500 uppercase mb-3">What to watch</h3>
              <div>
                <label className="block text-xs text-grey-600 mb-1">Entity Groups</label>
                <div className="flex flex-wrap gap-1.5">
                  {availableGroups.filter(g => g.is_active).map(g => (
                    <button key={g.group_id}
                      onClick={() => setSelectedGroups(prev => prev.includes(g.group_name) ? prev.filter(x => x !== g.group_name) : [...prev, g.group_name])}
                      className={`px-2 py-1 text-xs rounded-lg ${selectedGroups.includes(g.group_name) ? 'bg-purple-100 text-purple-700 font-medium' : 'bg-grey-100 text-grey-500 hover:bg-grey-200'}`}>
                      {g.group_name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Sources */}
          <div className="border-t border-grey-100 pt-4">
            <h3 className="text-xs font-semibold text-grey-500 uppercase mb-3">Sources</h3>
            <div className="flex flex-wrap gap-2">
              {availableSources.filter(s => s.adapter_name).map(s => (
                <label key={s.source_id} className="flex items-center gap-1.5 text-xs text-grey-700 cursor-pointer">
                  <input type="checkbox" checked={selectedSources.includes(s.adapter_name || s.source_name)}
                    onChange={e => { const n = s.adapter_name || s.source_name; setSelectedSources(prev => e.target.checked ? [...prev, n] : prev.filter(x => x !== n)); }}
                    className="h-3.5 w-3.5 rounded" />
                  {s.source_name}
                </label>
              ))}
            </div>
          </div>

          {/* Schedule */}
          <div className="border-t border-grey-100 pt-4">
            <h3 className="text-xs font-semibold text-grey-500 uppercase mb-3">Schedule</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <label className="text-xs text-grey-600 w-20">Timezone</label>
                <select value={timezone} onChange={e => setTimezone(e.target.value)} className="border border-grey-200 rounded px-2 py-1 text-xs bg-surface">
                  <option value="Australia/Sydney">Sydney (AEST/AEDT)</option>
                  <option value="Australia/Perth">Perth (AWST)</option>
                  <option value="Europe/London">London (GMT/BST)</option>
                  <option value="America/New_York">New York (EST/EDT)</option>
                  <option value="America/Chicago">Chicago (CST/CDT)</option>
                  <option value="Asia/Tokyo">Tokyo (JST)</option>
                  <option value="Asia/Hong_Kong">Hong Kong (HKT)</option>
                  <option value="Asia/Singapore">Singapore (SGT)</option>
                  <option value="UTC">UTC</option>
                </select>
              </div>
              {/* Polling windows — a time is polled only inside a window;
                  gaps between windows = no polling. */}
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                {windows.map((w, i) => (
                  <div key={i} className="border border-grey-200 rounded-lg p-2.5 space-y-2 bg-grey-50">
                    <div className="flex items-center justify-between gap-2">
                      <input
                        type="text"
                        value={w.label || ''}
                        onChange={e => patchWindow(i, { label: e.target.value })}
                        placeholder={`Window ${i + 1}`}
                        className="text-xs font-semibold text-grey-700 bg-transparent border-b border-transparent focus:border-grey-300 focus:outline-none flex-1 min-w-0"
                      />
                      <button onClick={() => removeWindow(i)} className="text-grey-400 hover:text-danger-600 text-sm leading-none" title="Remove window">✕</button>
                    </div>
                    {/* day toggles */}
                    <div className="flex gap-1">
                      {DAYS.map((day, di) => (
                        <button key={day} type="button" onClick={() => toggleWindowDay(i, day)}
                          className={`w-7 h-7 text-xs font-semibold rounded ${w.days.includes(day) ? 'bg-primary-100 text-primary-700' : 'bg-surface text-grey-400 hover:bg-grey-100'}`}>
                          {DAY_LABELS[di]}
                        </button>
                      ))}
                    </div>
                    {/* cadence type switch */}
                    <div className="flex gap-1">
                      {(['interval', 'explicit'] as const).map(ct => (
                        <button key={ct} type="button" onClick={() => setCadenceType(i, ct)}
                          className={`px-2 py-1 text-xs rounded ${w.cadence.type === ct ? 'bg-primary-600 text-white' : 'bg-surface text-grey-500 border border-grey-200'}`}>
                          {ct === 'interval' ? 'Interval' : 'Exact times'}
                        </button>
                      ))}
                    </div>
                    {/* cadence inputs */}
                    {w.cadence.type === 'interval' ? (
                      <div className="flex items-center gap-1.5 flex-wrap text-xs text-grey-500">
                        <input type="time" value={w.start || ''} onChange={e => patchWindow(i, { start: e.target.value })} className="border border-grey-200 rounded px-2 py-1 text-xs" />
                        <span>to</span>
                        <input type="time" value={w.end || ''} onChange={e => patchWindow(i, { end: e.target.value })} className="border border-grey-200 rounded px-2 py-1 text-xs" />
                        <span>every</span>
                        <input type="number" min={10} step={5} value={w.cadence.minutes ?? 30}
                          onChange={e => patchCadence(i, { minutes: Math.max(10, Number(e.target.value)) })}
                          className="border border-grey-200 rounded px-2 py-1 text-xs w-16" />
                        <span>min, anchored at :</span>
                        <input type="number" min={0} max={59} value={w.cadence.anchor_minute ?? 0}
                          onChange={e => patchCadence(i, { anchor_minute: Math.max(0, Math.min(59, Number(e.target.value))) })}
                          className="border border-grey-200 rounded px-2 py-1 text-xs w-14" />
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {(w.cadence.times || []).map((t, ti) => (
                          <span key={ti} className="flex items-center gap-1 bg-surface border border-grey-200 rounded px-1.5 py-0.5">
                            <input type="time" value={t}
                              onChange={e => { const times = [...(w.cadence.times || [])]; times[ti] = e.target.value; patchCadence(i, { times }); }}
                              className="text-xs bg-transparent focus:outline-none w-[72px]" />
                            <button type="button" onClick={() => patchCadence(i, { times: (w.cadence.times || []).filter((_, x) => x !== ti) })}
                              className="text-grey-400 hover:text-danger-600 text-xs leading-none">✕</button>
                          </span>
                        ))}
                        <button type="button" onClick={() => patchCadence(i, { times: [...(w.cadence.times || []), '12:00'] })}
                          className="px-2 py-0.5 text-xs rounded bg-surface border border-grey-200 text-primary-600 hover:bg-primary-50">+ time</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button type="button" onClick={addWindow}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium">+ Add window</button>

              {/* Live preview of the next fires under the current (unsaved) windows */}
              <div className="border-t border-grey-100 pt-2 mt-1">
                <span className="text-[10px] text-grey-400 uppercase font-semibold">Next fires</span>
                <div className="flex flex-wrap gap-1 mt-1">
                  {previewFires.length > 0 ? previewFires.map(f => (
                    <span key={f} className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-grey-100 text-grey-600">{f}</span>
                  )) : (
                    <span className="text-[11px] text-warning-500 italic">No fires resolved — check the windows above.</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Limits — only for research/backfill/gap_fill types */}
          {['research', 'backfill', 'gap_fill'].includes(needType) && (
            <details className="border-t border-grey-100 pt-4">
              <summary className="text-xs font-semibold text-grey-500 uppercase cursor-pointer">Limits</summary>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div>
                  <label className="block text-xs text-grey-600 mb-1">Daily limit</label>
                  <input type="number" value={dailyLimit} onChange={e => setDailyLimit(e.target.value)} placeholder="None" className="w-full border border-grey-200 rounded px-2 py-1 text-xs" />
                </div>
                <div>
                  <label className="block text-xs text-grey-600 mb-1">Total limit</label>
                  <input type="number" value={totalLimit} onChange={e => setTotalLimit(e.target.value)} placeholder="None" className="w-full border border-grey-200 rounded px-2 py-1 text-xs" />
                </div>
                <div>
                  <label className="block text-xs text-grey-600 mb-1">Date mode</label>
                  <select value={dateMode} onChange={e => setDateMode(e.target.value)} className="w-full border border-grey-200 rounded px-2 py-1 text-xs bg-surface">
                    <option value="publish_date">Publish date</option>
                    <option value="source_date">Source date</option>
                  </select>
                </div>
              </div>
            </details>
          )}
      </ModalBody>

      <ModalFooter>
        <button onClick={onClose} className="px-4 py-2 text-sm text-grey-600">Cancel</button>
        <button onClick={handleSubmit} disabled={isSubmitting || !name}
          className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
          {isSubmitting ? 'Saving...' : isEditing ? 'Update' : 'Create'}
        </button>
      </ModalFooter>
    </Modal>
  );
}


// ============================================================================
// ENTITY GROUPS TAB
// ============================================================================

function EntityGroupsTab({ groups, isLoading }: { groups: EntityGroup[]; isLoading: boolean }) {
  const [showModal, setShowModal] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const queryClient = useQueryClient();

  if (isLoading) return <Loading />;
  if (!groups.length) return <Empty icon={Users} message="No entity groups configured" />;

  return (
    <>
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700">
          <Plus className="h-4 w-4" /> New Group
        </button>
      </div>
      <div className="space-y-3">
        {groups.map(group => (
          <div key={group.group_id}>
            <div className="flex items-center gap-4 p-4 bg-surface rounded-lg border border-grey-200 hover:border-primary-300 cursor-pointer"
              onClick={() => setExpandedGroup(expandedGroup === group.group_id ? null : group.group_id)}>
              <Users className={`h-5 w-5 ${group.is_active ? 'text-primary-600' : 'text-grey-400'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-grey-900">{group.group_name}</span>
                  <StatusBadge active={group.is_active} />
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${group.group_type === 'dynamic' ? 'bg-purple-100 text-purple-700' : 'bg-grey-100 text-grey-600'}`}>{group.group_type}</span>
                  {group.is_fast_lane && (
                    <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700" title="Fast-lane: broad scan skips these entities; a dedicated coverage need fetches every filing">
                      <Zap className="h-3 w-3" /> fast-lane
                    </span>
                  )}
                  {group.coverage_policy && (
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-primary-50 text-primary-700" title="Guaranteed coverage — auto-provisions a coverage:<group> monitor">
                      coverage · {group.coverage_policy.cadence_minutes}m · {group.coverage_policy.editorial_guarantee}
                    </span>
                  )}
                </div>
                {group.description && <p className="text-xs text-grey-500 mt-0.5 truncate">{group.description}</p>}
              </div>
              <DynamicOrStaticCount group={group} />
              <ChevronRight className={`h-4 w-4 text-grey-400 transition-transform ${expandedGroup === group.group_id ? 'rotate-90' : ''}`} />
            </div>
            {expandedGroup === group.group_id && (
              <>
                <CoveragePolicyEditor group={group} />
                <GroupMemberPanel group={group} />
              </>
            )}
          </div>
        ))}
      </div>
      {showModal && <GroupCreateModal onClose={() => setShowModal(false)} />}
    </>
  );
}

// Coverage & fast-lane editor for a group. Declares the group's coverage
// policy (FAST_LANE_COVERAGE_GUARANTEE_PLAN.md Phase 1); the backend
// auto-provisions/updates the managed `coverage:<group>` monitor from it, so
// this is the single tuning surface — the derived monitor is read-only in the
// Monitors tab.
const DEFAULT_COVERAGE_POLICY: CoveragePolicy = {
  cadence_minutes: 15, sources: ['market_index'], priority: 15,
  budget_exempt: true, editorial_guarantee: 'sector_routed',
};

function PolicyField({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-xs font-medium text-grey-500 mb-1">{label}</label>
      {children}
    </div>
  );
}

function CoveragePolicyEditor({ group }: { group: EntityGroup }) {
  const queryClient = useQueryClient();
  const [policy, setPolicy] = useState<CoveragePolicy | null>(group.coverage_policy ?? null);
  const [fastLane, setFastLane] = useState<boolean>(group.is_fast_lane ?? false);

  // Re-sync local state when the group refetches after a save.
  useEffect(() => {
    setPolicy(group.coverage_policy ?? null);
    setFastLane(group.is_fast_lane ?? false);
  }, [group.group_id, group.updated_at]);

  const mut = useMutation({
    mutationFn: (body: EntityGroupUpdate) => updateEntityGroup(group.group_id, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['entity-groups'] }),
  });

  const dirty =
    fastLane !== (group.is_fast_lane ?? false) ||
    JSON.stringify(policy) !== JSON.stringify(group.coverage_policy ?? null);

  const set = (patch: Partial<CoveragePolicy>) =>
    setPolicy(p => ({ ...(p ?? DEFAULT_COVERAGE_POLICY), ...patch }));

  return (
    <div className="mt-1 p-4 bg-grey-50 rounded-lg border border-grey-200 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-grey-500">Coverage &amp; Fast-Lane</span>
        {dirty && (
          <button onClick={() => mut.mutate({ is_fast_lane: fastLane, coverage_policy: policy })}
            disabled={mut.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
            {mut.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Save
          </button>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm text-grey-700">
        <input type="checkbox" checked={fastLane} onChange={e => setFastLane(e.target.checked)} />
        <Zap className="h-3.5 w-3.5 text-amber-500" />
        Fast-lane — broad scan skips these entities; a dedicated coverage need fetches every filing
      </label>

      <label className="flex items-center gap-2 text-sm text-grey-700">
        <input type="checkbox" checked={!!policy}
          onChange={e => setPolicy(e.target.checked ? (group.coverage_policy ?? DEFAULT_COVERAGE_POLICY) : null)} />
        Guaranteed coverage — auto-provisions a <code className="text-xs px-1 bg-grey-100 rounded">coverage:{group.group_name}</code> monitor
      </label>

      {policy && (
        <div className="grid grid-cols-2 gap-3 pl-6">
          <PolicyField label="Cadence (minutes)">
            <input type="number" min={1} value={policy.cadence_minutes}
              onChange={e => set({ cadence_minutes: Number(e.target.value) })}
              className="w-full px-2 py-1 text-sm border border-grey-300 rounded" />
          </PolicyField>
          <PolicyField label="Priority (lower = higher)">
            <input type="number" min={1} max={100} value={policy.priority}
              onChange={e => set({ priority: Number(e.target.value) })}
              className="w-full px-2 py-1 text-sm border border-grey-300 rounded" />
          </PolicyField>
          <PolicyField label="Sources (comma-separated)">
            <input type="text" value={(policy.sources ?? []).join(', ')}
              onChange={e => set({ sources: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
              className="w-full px-2 py-1 text-sm border border-grey-300 rounded" />
          </PolicyField>
          <PolicyField label="Editorial guarantee">
            <select value={policy.editorial_guarantee}
              onChange={e => set({ editorial_guarantee: e.target.value as EditorialGuarantee })}
              className="w-full px-2 py-1 text-sm border border-grey-300 rounded bg-surface">
              <option value="all_sites">all_sites — article on every site</option>
              <option value="sector_routed">sector_routed — via sector rules</option>
              <option value="none">none — ingest only</option>
            </select>
          </PolicyField>
          <label className="flex items-center gap-2 text-sm text-grey-700 col-span-2">
            <input type="checkbox" checked={policy.budget_exempt}
              onChange={e => set({ budget_exempt: e.target.checked })} />
            Budget-exempt — not subject to the discovery daily cap
          </label>
        </div>
      )}
      {mut.isError && <p className="text-xs text-red-600">Save failed — check inputs and retry.</p>}
    </div>
  );
}

function GroupMemberPanel({ group }: { group: EntityGroup }) {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [discoveredEntity, setDiscoveredEntity] = useState<Record<string, any> | null>(null);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const membersQuery = useQuery({ queryKey: ['group-members', group.group_id], queryFn: () => getGroupMembers(group.group_id), enabled: group.group_type === 'static' });
  const resolveQuery = useQuery({ queryKey: ['group-resolve', group.group_id], queryFn: () => resolveGroup(group.group_id), enabled: group.group_type === 'dynamic' });
  const searchQuery = useQuery({ queryKey: ['entity-search', searchTerm], queryFn: () => listEntities({ search: searchTerm, page_size: 10 }), enabled: searchTerm.length >= 2 });
  const addMut = useMutation({ mutationFn: (ids: string[]) => addGroupMembers(group.group_id, ids), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['group-members', group.group_id] }); queryClient.invalidateQueries({ queryKey: ['entity-groups'] }); queryClient.invalidateQueries({ queryKey: ['entities'] }); setSearchTerm(''); setDiscoveredEntity(null); } });
  const removeMut = useMutation({ mutationFn: (id: string) => removeGroupMember(group.group_id, id), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['group-members', group.group_id] }); queryClient.invalidateQueries({ queryKey: ['entity-groups'] }); } });

  // When search returns no results and query looks like a ticker, try exchange lookup
  const noResults = searchTerm.length >= 2 && searchQuery.data && searchQuery.data.items.length === 0;
  const looksLikeTicker = /^[A-Za-z]{2,5}$/.test(searchTerm.trim());

  const handleDiscover = async () => {
    setIsDiscovering(true);
    setDiscoveredEntity(null);
    try {
      const result = await lookupEntity(searchTerm.trim().toUpperCase());
      if (result.display_name) setDiscoveredEntity(result);
    } catch { /* no result */ }
    setIsDiscovering(false);
  };

  const handleCreateAndAdd = async () => {
    if (!discoveredEntity) return;
    const ticker = discoveredEntity.ticker || searchTerm.trim().toUpperCase();
    try {
      const metadata: Record<string, any> = {};
      for (const k of ['sector', 'gics_sector', 'market_cap_tier', 'market_cap', 'exchange', 'website_url', 'industry_group', 'regions', 'date_listed']) {
        if (discoveredEntity[k]) metadata[k] = discoveredEntity[k];
      }
      const created = await createEntity({
        entity_type: (discoveredEntity.entity_type || 'asx_company') as any,
        canonical_identifier: ticker,
        display_name: discoveredEntity.display_name,
        description: discoveredEntity.description || '',
        metadata,
      });
      if (created?.entity_id) {
        addMut.mutate([created.entity_id]);
      }
    } catch (e: any) {
      // Entity may already exist (409) — search for it and add
      try {
        const existing = await listEntities({ search: ticker, page_size: 1 });
        if (existing.items.length > 0) {
          addMut.mutate([existing.items[0].entity_id]);
          return;
        }
      } catch { /* fall through */ }
      alert(e?.response?.data?.detail || e?.message || 'Failed to create entity');
    }
  };

  if (group.group_type === 'dynamic') {
    return (
      <div className="ml-9 mt-1 p-4 bg-grey-50 rounded-lg border border-grey-200">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-grey-500">{resolveQuery.data ? `${resolveQuery.data.resolved_count} entities` : 'Dynamic group'}</span>
          <button onClick={() => queryClient.invalidateQueries({ queryKey: ['group-resolve', group.group_id] })} className="flex items-center gap-1 text-xs text-primary-600"><RefreshCw className="h-3 w-3" /> Refresh</button>
        </div>
        {resolveQuery.isLoading && <div className="flex items-center gap-2 text-sm text-grey-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Resolving...</div>}
        {resolveQuery.data?.entities && resolveQuery.data.entities.length > 0 && (
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {resolveQuery.data.entities.map((e) => (
              <div key={e.entity_id} className="flex items-center justify-between px-3 py-1.5 bg-surface rounded border border-grey-200">
                <span className="text-sm"><span className="font-medium">{e.canonical_identifier}</span> <span className="text-grey-500 ml-1">{e.display_name}</span></span>
                <span className="text-xs text-grey-400">{e.entity_type}</span>
              </div>
            ))}
          </div>
        )}
        {resolveQuery.data && !resolveQuery.data.entities && (
          <div className="text-xs text-grey-400">Resolved {resolveQuery.data.resolved_count} entities (details not available in this view)</div>
        )}
      </div>
    );
  }

  return (
    <div className="ml-9 mt-1 p-4 bg-grey-50 rounded-lg border border-grey-200">
      <div className="relative mb-3">
        <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-grey-400" />
        <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search entities to add..." className="w-full pl-8 pr-3 py-2 text-sm border border-grey-300 rounded-lg" />
      </div>
      {searchTerm.length >= 2 && searchQuery.data && (
        <div className="mb-3 max-h-48 overflow-y-auto border border-grey-200 rounded-lg bg-surface">
          {searchQuery.data.items.map(e => (
            <button key={e.entity_id} onClick={() => addMut.mutate([e.entity_id])} className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-grey-50 border-b border-grey-100 last:border-0">
              <span><span className="font-medium">{e.canonical_identifier}</span> <span className="text-grey-500">{e.display_name}</span></span>
              <Plus className="h-3.5 w-3.5 text-primary-600" />
            </button>
          ))}

          {/* No entity found — offer exchange lookup */}
          {noResults && looksLikeTicker && !discoveredEntity && (
            <div className="px-3 py-3 border-t border-grey-100">
              <p className="text-xs text-grey-500 mb-2">No entity found for "{searchTerm.toUpperCase()}"</p>
              <button onClick={handleDiscover} disabled={isDiscovering}
                className="flex items-center gap-1.5 text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50">
                {isDiscovering ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}
                {isDiscovering ? 'Searching exchanges...' : `Search ASX/NSX for ${searchTerm.toUpperCase()}`}
              </button>
            </div>
          )}

          {/* Discovered entity — offer to create & add */}
          {discoveredEntity && (
            <div className="px-3 py-3 border-t border-primary-100 bg-primary-50">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-grey-900">{discoveredEntity.display_name}</p>
                  <p className="text-xs text-grey-500">
                    {discoveredEntity.exchange}:{discoveredEntity.ticker}
                    {discoveredEntity.gics_sector && ` — ${discoveredEntity.gics_sector}`}
                    {discoveredEntity.market_cap_tier && ` (${discoveredEntity.market_cap_tier})`}
                  </p>
                  {discoveredEntity.description && <p className="text-xs text-grey-400 mt-0.5">{discoveredEntity.description}</p>}
                  <p className="text-[10px] text-grey-400 mt-0.5">via {discoveredEntity.sources_checked?.join(', ')}</p>
                </div>
                <button onClick={handleCreateAndAdd}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex-shrink-0">
                  <Plus className="h-3 w-3" /> Create & Add
                </button>
              </div>
            </div>
          )}

          {noResults && !looksLikeTicker && !discoveredEntity && (
            <div className="px-3 py-2 text-xs text-grey-400">No entities found</div>
          )}
        </div>
      )}
      <div className="space-y-1">
        {(membersQuery.data || []).length === 0 && <div className="text-xs text-grey-400">No members yet.</div>}
        {(membersQuery.data || []).map(m => (
          <div key={m.entity_id} className="flex items-center justify-between px-3 py-1.5 bg-surface rounded border border-grey-200">
            <span className="text-sm"><span className="font-medium">{m.canonical_identifier}</span> <span className="text-grey-500 ml-1">{m.display_name}</span></span>
            <button onClick={() => removeMut.mutate(m.entity_id)} className="p-1 text-grey-400 hover:text-danger-600"><Trash2 className="h-3.5 w-3.5" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function GroupCreateModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [groupType, setGroupType] = useState('static');
  const [criteriaType, setCriteriaType] = useState('entity_type');
  const [criteriaValue, setCriteriaValue] = useState('asx_company');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const criteria = groupType === 'dynamic' ? { [criteriaType]: criteriaValue } : undefined;
      await createEntityGroup({ group_name: name, description: description || undefined, group_type: groupType, dynamic_criteria: criteria });
      queryClient.invalidateQueries({ queryKey: ['entity-groups'] });
      onClose();
    } catch { setIsSubmitting(false); }
  };

  return (
    <Modal onClose={onClose} size="lg">
      <ModalHeader>
        <h2 className="text-lg font-bold text-grey-900">New Entity Group</h2>
        <button onClick={onClose} className="text-grey-400 hover:text-grey-600 touch-target"><X className="h-5 w-5" /></button>
      </ModalHeader>
      <ModalBody className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-grey-600 mb-1">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-grey-600 mb-1">Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional" className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-grey-600 mb-1">Type</label>
            <select value={groupType} onChange={e => setGroupType(e.target.value)} className="w-full border border-grey-200 rounded-lg px-3 py-2 text-sm bg-surface">
              <option value="static">Static (manual members)</option>
              <option value="dynamic">Dynamic (auto-resolve)</option>
            </select>
          </div>
          {groupType === 'dynamic' && (
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-grey-600 mb-1">Criteria</label>
                <div className="flex gap-2">
                  <select value={criteriaType} onChange={e => setCriteriaType(e.target.value)} className="border border-grey-200 rounded-lg px-3 py-2 text-sm bg-surface">
                    <option value="entity_type">Entity Type</option>
                    <option value="has_tenant_subscription">Has Subscription</option>
                  </select>
                  <select value={criteriaValue} onChange={e => setCriteriaValue(e.target.value)} className="flex-1 border border-grey-200 rounded-lg px-3 py-2 text-sm bg-surface">
                    {criteriaType === 'entity_type'
                      ? ['asx_company','nsx_company','lse_stock','tsx_stock','company'].map(v => <option key={v} value={v}>{v}</option>)
                      : ['true','false'].map(v => <option key={v} value={v}>{v}</option>)
                    }
                  </select>
                </div>
              </div>
            </div>
          )}
      </ModalBody>
      <ModalFooter>
        <button onClick={onClose} className="px-4 py-2 text-sm text-grey-600">Cancel</button>
        <button onClick={handleSubmit} disabled={isSubmitting || !name} className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50">
          {isSubmitting ? 'Creating...' : 'Create'}
        </button>
      </ModalFooter>
    </Modal>
  );
}


// ============================================================================
// SYSTEM CONFIG TAB — read-only reference
// ============================================================================

function ContentProductRow({ product }: { product: any }) {
  const queryClient = useQueryClient();
  const promoteEligible =
    product.product_type === 'article' || product.product_type === 'intelligence_signal';
  const flagOn = product.auto_promote_to_live === true;
  const updateMut = useMutation({
    mutationFn: (next: boolean) =>
      updateContentProduct(product.product_id, { auto_promote_to_live: next }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['content-products'] }),
    onError: (err: any) => {
      // eslint-disable-next-line no-alert
      alert(`Failed to update: ${err?.response?.data?.detail || err?.message || 'unknown'}`);
    },
  });

  return (
    <div className="flex items-center gap-3 p-3 bg-surface rounded-lg border border-grey-200">
      <Package className={`h-4 w-4 ${product.is_active ? 'text-primary-600' : 'text-grey-400'}`} />
      <span className="text-sm font-medium text-grey-900">{product.product_name}</span>
      <span className="px-1.5 py-0.5 text-[10px] bg-blue-50 text-blue-700 rounded">{product.product_type}</span>
      <span className="px-1.5 py-0.5 text-[10px] bg-grey-100 text-grey-600 rounded">{product.approval_mode}</span>
      {product.egress_component_name && (
        <span className="text-xs text-grey-400">→ {product.egress_component_name}</span>
      )}
      <StatusBadge active={product.is_active} />
      {promoteEligible && (
        <label
          className="ml-auto flex items-center gap-1.5 text-xs cursor-pointer"
          title={
            flagOn
              ? 'Successful test publish will auto-enqueue a main_branch promote'
              : 'Successful test publish will stay on test (manual promote required)'
          }
        >
          <input
            type="checkbox"
            checked={flagOn}
            disabled={updateMut.isPending}
            onChange={e => updateMut.mutate(e.target.checked)}
            className="h-3.5 w-3.5 rounded"
          />
          <span className={flagOn ? 'text-success-700 font-semibold' : 'text-grey-500'}>
            auto-promote to live
          </span>
        </label>
      )}
    </div>
  );
}

function SystemConfigTab({ sources, products, rules, isLoading }: {
  sources: IngressSource[]; products: any[]; rules: any[]; isLoading: boolean;
}) {
  if (isLoading) return <Loading />;

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-sm font-semibold text-grey-700 mb-3">Sources ({sources.length})</h3>
        <div className="space-y-2">
          {sources.map(s => (
            <div key={s.source_id} className="flex items-center gap-3 p-3 bg-surface rounded-lg border border-grey-200">
              <Database className={`h-4 w-4 ${s.is_active ? 'text-info-600' : 'text-grey-400'}`} />
              <span className="text-sm font-medium text-grey-900">{s.source_name}</span>
              <StatusBadge active={s.is_active} />
              {s.adapter_name && <span className="px-1.5 py-0.5 text-[10px] font-mono bg-grey-100 text-grey-600 rounded">{s.adapter_name}</span>}
              <span className="text-xs text-grey-400 ml-auto">
                {s.fetch_statistics?.total_fetched != null && `${s.fetch_statistics.total_fetched} fetched`}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-grey-700 mb-3">Content Products ({products.length})</h3>
        <div className="space-y-2">
          {products.map((p: any) => (
            <ContentProductRow key={p.product_id} product={p} />
          ))}
        </div>
      </div>

      <div>
        <h3 className="text-sm font-semibold text-grey-700 mb-3">Routing Rules ({rules.length})</h3>
        <div className="space-y-2">
          {rules.map((r: any) => (
            <div key={r.rule_id} className="flex items-center gap-3 p-3 bg-surface rounded-lg border border-grey-200">
              <GitBranch className={`h-4 w-4 ${r.is_active ? 'text-success-600' : 'text-grey-400'}`} />
              <span className="text-sm font-medium text-grey-900">{r.rule_name}</span>
              <span className="text-xs text-grey-500">P{r.priority}</span>
              <span className="text-xs text-grey-400">→ {r.product_name}</span>
              <StatusBadge active={r.is_active} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// Shared
// ============================================================================

function DynamicOrStaticCount({ group }: { group: EntityGroup }) {
  const resolveQuery = useQuery({
    queryKey: ['group-resolve', group.group_id],
    queryFn: () => resolveGroup(group.group_id),
    enabled: group.group_type === 'dynamic',
    staleTime: 60000,
  });

  if (group.group_type === 'dynamic') {
    if (resolveQuery.isLoading) return <span className="text-xs text-grey-400">resolving...</span>;
    if (resolveQuery.data) return <span className="text-sm text-purple-600">{resolveQuery.data.resolved_count} entities</span>;
    return <span className="text-sm text-grey-400">dynamic</span>;
  }

  return <span className="text-sm text-grey-500">{group.member_count ?? 0} members</span>;
}

function StatusBadge({ active }: { active: boolean }) {
  return active
    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-success-100 text-success-700"><CheckCircle2 className="h-3 w-3" /> Active</span>
    : <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-grey-100 text-grey-500"><XCircle className="h-3 w-3" /> Inactive</span>;
}

function TypeBadge({ type }: { type: string }) {
  const colors: Record<string, string> = {
    company_coverage: 'bg-primary-100 text-primary-700', trend_reactive: 'bg-purple-100 text-purple-700',
    content_type: 'bg-blue-100 text-blue-700', research: 'bg-success-100 text-success-700',
    backfill: 'bg-warning-100 text-warning-700', gap_fill: 'bg-orange-100 text-orange-700',
    intraday_discovery: 'bg-indigo-100 text-indigo-700',
  };
  return <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colors[type] || 'bg-grey-100 text-grey-600'}`}>{type.replace(/_/g, ' ')}</span>;
}

function Loading() { return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 text-grey-400 animate-spin" /></div>; }
function Empty({ icon: Icon, message }: { icon: typeof Radio; message: string }) { return <div className="flex flex-col items-center justify-center py-12 text-grey-400"><Icon className="h-8 w-8 mb-2" /><p className="text-sm">{message}</p></div>; }
